'use client';
import useImageWidthResponsive from '../hooks/useImageWidthResponsive';
import React, { useState } from 'react';
import { hasCldImage, TypeBlockPageIntro, TypePageWithoutUnresolvedLink } from '@fm-nx/contentful-types';
import BackLinkPage from '../back-link-page/back-link-page';
import AnimationImage from '../animation-image/animation-image';
import { CldImage } from 'next-cloudinary';

type PageIntroImageFullProps = {
    intro: TypeBlockPageIntro<'WITHOUT_UNRESOLVABLE_LINKS'>;
    page?: TypePageWithoutUnresolvedLink;
};

// technically this is a landing page intro I think
// Right now there is only one option: blockImageOverlayIntro
export const PageIntroImageFull = (props: PageIntroImageFullProps) => {
    const { intro, page } = props;
    const { fields } = intro;
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const { containerRef, width } = useImageWidthResponsive();
    const imageCloudinary = hasCldImage(fields.image?.fields) ? fields.image?.fields.cloudinaryMedia[0] : null;

    if (!fields) return null;

    return (
        <div className="relative">
            <div className="relative w-full h-[424px] md:h-screen md:max-h-268" ref={containerRef}>
                {/* TODO: Why does webp make the image load slower */}
                {imageCloudinary && (
                    <div className="absolute inset-0">
                        <AnimationImage isImageLoaded={isImageLoaded}>
                            <div className="z-10 absolute bottom-0 w-full bg-linear-to-b from-gray-900/50 to-transparent h-[424px] md:h-screen md:max-h-268"></div>
                            <CldImage
                                className="z-0 object-cover"
                                fill
                                src={`${imageCloudinary.public_id}`}
                                alt={fields.image?.fields?.title || ''}
                                sizes={width}
                                priority
                                onLoad={() => setIsImageLoaded(true)}
                                format="auto" // Automatically use WebP or AVIF based on browser support
                                quality="auto" // Automatically adjust quality based on network and device
                                dpr="auto" // Automatically adjust for device pixel ratio
                                fetchPriority="high" // This is a critical above-the-fold image
                                loading="eager" // Load immediately as it's above the fold
                            />
                        </AnimationImage>
                    </div>
                )}
            </div>
            <div className="relative z-10 -mt-[180px] before:content=[''] before:-z-10 before:absolute before:top-0 before:w-full before:h-[180px] before:bg-linear-to-t before:from-gray-900/80 before:to-transparent md:-mt-[220px] md:before:h-[220px] lg:-mt-[238px] lg:before:h-[3] xl:-mt-[296px] xl:before:h-[296px] xl:overflow-hidden">
                <div className="w-full mx-auto mb-2 max-w-container">
                    <div className="mx-6 md:mx-0 md:grid md:grid-cols-14">
                        <div className="h-[180px] md:col-span-4 md:col-start-2 xl:col-span-3 xl:col-start-2">
                            <div className="flex items-center pt-9 md:pt-9 lg:pt-13 xl:pt-21">
                                {page && <BackLinkPage mode="dark" />}
                            </div>
                        </div>
                        <div className="md:col-span-9 md:col-start-6 md:bg-white md:min-h-[3] xl:min-h-[296px] xl:col-span-10 xl:col-start-5 xl:relative xl:before:content=[''] xl:before:-z-10 xl:before:absolute xl:before:top-0 xl:before:left-full xl:before:bg-white xl:before:w-screen xl:before:h-full">
                            <div className="flex flex-col py-8 space-y-6 md:px-8 lg:pt-12 lg:pl-16 xl:pt-16 xl:pl-20 2xl:pl-28">
                                <p className="z-10 text-gray-900 h5 overline">{fields?.overline}</p>
                                <h1 className="text-gray-900 h-xl md:h-xxl">{fields?.headerText}</h1>
                                <p className="text-gray-900 h-xs xl:h-md">{fields?.headerParagraph}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PageIntroImageFull;
